import React from 'react';
import { useRecoilState } from 'recoil';
import { languageAtom } from 'atoms/language';
import { useSiteMetadata } from 'hooks/useSiteMetadata';
import { useBlogLogo } from 'hooks/useBlogLogo';
import { getSrc } from 'gatsby-plugin-image';

interface Props {
  title?: string;
  description?: string;
  image?: string;
}

const SEO = ({ title, description = '', image }: Props) => {
  const siteMetadata = useSiteMetadata();
  const [lang] = useRecoilState(languageAtom);
  const { openGraphImage } = useBlogLogo();
  const openGraphImageSrc = openGraphImage?.childImageSharp != null ? getSrc(openGraphImage.childImageSharp) : '';

  const metaTitle = title ?? siteMetadata.title;
  const metaDescription = description ?? siteMetadata.description;
  const metaImage = image ?? openGraphImageSrc;

  const siteData = {
    '@context': 'https://schema.org',
    '@type': 'WebPage',
    name: siteMetadata.title,
    url: siteMetadata.siteUrl,
    mainEntityOfPage: siteMetadata.siteUrl,
    description: siteMetadata.description,
    author: {
      '@type': 'Person',
      name: siteMetadata.author.name,
    },
    creator: {
      '@type': 'Person',
      name: siteMetadata.author.name,
    },
    image: {
      '@type': 'ImageObject',
      url: openGraphImageSrc,
    },
  };

  const orgData = {
    '@context': 'https://schema.org',
    '@type': 'Organization',
    url: siteMetadata.siteUrl,
    logo: openGraphImageSrc,
  };

  return (
    <>
      <html lang={lang} />
      <title>{metaTitle}</title>
      <meta name="description" content={metaDescription} />

      <meta property="og:title" content={metaTitle} />
      <meta property="og:description" content={metaDescription} />
      <meta property="og:image" content={metaImage} />
      <meta property="og:type" content="website" />

      <meta property="twitter:title" content={metaTitle} />
      <meta property="twitter:description" content={metaDescription} />
      <meta property="twitter:image" content={metaImage} />

      <meta name="naver-site-verification" content="cb5242f923988068cba0c121602aeb95fcc4b69c" />
      <meta name="google-site-verification" content="oDn8TK5EJ-ZVoh3XGbemEEvoGi9dCYSffzj7VUfrG3Q" />
      <meta name="google-adsense-account" content="ca-pub-4372275195526937" />

      <script type="application/ld+json">{JSON.stringify(siteData)}</script>
      <script type="application/ld+json">{JSON.stringify(orgData)}</script>

      <script
        async
        src="https://pagead2.googlesyndication.com/pagead/js/adsbygoogle.js?client=ca-pub-4372275195526937"
        crossOrigin="anonymous"
      />
      <link rel="preconnect" href="https://fonts.googleapis.com" />
      <link rel="preconnect" href="https://fonts.gstatic.com" crossOrigin="anonymous" />
      <link
        href="https://fonts.googleapis.com/css2?family=Birthstone&family=Nanum+Myeongjo&family=Noto+Sans:ital,wght@0,100..900;1,100..900&display=swap"
        rel="stylesheet"
      />
    </>
  );
};

export default SEO;
